import { useEffect, useRef, useState } from 'react';
import { attachmentSlotsLeft, capIncomingAttachments } from '../../../lib/attachment-cap';

// Files picked/pasted/dropped on the home stage here as chips — nothing navigates,
// the typed draft survives, and they ride along on Send. Page-level paste + drop is
// gated to a CLOSED chat (an open chat owns its own paste/drop surface); text pastes
// fall through untouched, only files stage.
export function useStagedFiles(chatClosed: boolean) {
  const [staged, setStaged] = useState<File[]>([]);
  // Capped at the server's attachment cap (lib/attachment-cap.ts), like the chat composer: the files that
  // fit are staged, and `limitNotice` says the rest were not.
  const [limitNotice, setLimitNotice] = useState(false);

  const stageFiles = (files: File[]) => {
    if (files.length === 0) return;
    const { accepted, refused } = capIncomingAttachments(staged.length, files);
    setLimitNotice(refused > 0);
    if (accepted.length === 0) return;
    setStaged((prev) => [...prev, ...accepted]);
  };

  /** True when the attach button should not open a picker: the draft is full. Raises the notice. */
  const refuseWhenFull = (): boolean => {
    if (attachmentSlotsLeft(staged.length) > 0) return false;
    setLimitNotice(true);
    return true;
  };

  const removeAt = (index: number) => {
    setStaged((prev) => prev.filter((_, j) => j !== index));
    setLimitNotice(false);
  };
  const clear = () => {
    setStaged([]);
    setLimitNotice(false);
  };

  const stageRef = useRef(stageFiles);
  stageRef.current = stageFiles;
  useEffect(() => {
    if (!chatClosed) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (files.length === 0) return;
      e.preventDefault();
      stageRef.current(files);
    };
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      stageRef.current(Array.from(e.dataTransfer.files ?? []));
    };
    document.addEventListener('paste', onPaste);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [chatClosed]);

  return { staged, stageFiles, removeAt, clear, limitNotice, refuseWhenFull };
}
